import { useRef, useEffect } from "react";
import { MapContainer, TileLayer, FeatureGroup, Marker, Popup, useMap } from "react-leaflet";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";

// 🔧 Shared maximum zoom level
const MAX_ZOOM = 15;

// Define a custom SVG icon
const customMarker = new L.DivIcon({
  html: `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24">
      <path fill="#BF2424" d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5" fill="white"/>
    </svg>
  `,
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

// 📍 Fit bounds to all markers, capped at MAX_ZOOM
function FitBounds({ featureGroupRef, props }) {
  const map = useMap();

  useEffect(() => {
    if (featureGroupRef.current && props.length > 0) {
      const bounds = featureGroupRef.current.getBounds();
      if (bounds.isValid()) {
        map.flyToBounds(bounds, {
          padding: [20, 20],
          animate: true,
          maxZoom: MAX_ZOOM, // 👈 limit zoom here
        });
      }
    }
  }, [props, featureGroupRef, map]);

  return null;
}

// 🧭 Zoom to hovered marker, respecting same MAX_ZOOM
function ZoomToMarker({ hoverCoords, groupRef }) {
  const map = useMap();

  useEffect(() => {
    if (hoverCoords) {
      map.flyTo(hoverCoords, MAX_ZOOM, { animate: true }); // 👈 same max zoom
    } else if (groupRef.current) {
      const bounds = groupRef.current.getBounds();
      if (bounds.isValid()) {
        map.flyToBounds(bounds, {
          padding: [20, 20],
          animate: true,
          maxZoom: MAX_ZOOM, // 👈 same here
        });
      }
    }
  }, [hoverCoords, map, groupRef]);

  return null;
}

const Map = ({ props, hoverCoords }) => {
  const mapRef = useRef(null);
  const groupRef = useRef(null);
  const defaultCenter = [39.7508289, -75.5375705];
  const defaultZoom = 8;

  return (
    <MapContainer
      center={defaultCenter}
      zoom={defaultZoom}
      zoomSnap={0.25}
      zoomControl={false}
      dragging={false}
      doubleClickZoom={false}
      scrollWheelZoom={false}
      attributionControl={false}
      ref={mapRef}
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
      />

      <FeatureGroup ref={groupRef} name="Properties">
        {props.map((prop) => (
          <Marker
            key={prop.id}
            position={[Number(prop.acf.latitude), Number(prop.acf.longitude)]}
            icon={customMarker}
          >
            <Popup>{prop.title.rendered}</Popup>
          </Marker>
        ))}
      </FeatureGroup>

      {/* 🔍 Hover and FitBounds logic */}
      <ZoomToMarker hoverCoords={hoverCoords} groupRef={groupRef} />
      <FitBounds featureGroupRef={groupRef} props={props} />
    </MapContainer>
  );
};

export default Map;
